import { useEffect, useMemo, useState } from 'react'
import type { TClub } from '@schemas'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useClubs } from '@/api/hooks'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'

type ClubSelectorProps = {
  organizationId?: string | null
  hasOrganization: boolean
  isOnline: boolean
  value: string
  onValueChange: (value: string) => void
  selectedClubId: string | null
  onClubIdChange: (clubId: string | null) => void
  disabled?: boolean
  placeholder?: string
}

export function ClubSelector({
  organizationId,
  hasOrganization,
  isOnline,
  value,
  onValueChange,
  selectedClubId,
  onClubIdChange,
  disabled,
  placeholder = 'Search clubs…',
}: ClubSelectorProps) {
  const [open, setOpen] = useState(false)
  const trimmedInput = value.trim()
  const debouncedQuery = useDebouncedValue(trimmedInput, 250)
  const canSearch = isOnline && !disabled && organizationId !== undefined

  const clubsQuery = useClubs(
    canSearch
      ? {
          organizationId: organizationId ?? null,
          q: debouncedQuery || undefined,
          limit: 50,
          includeGlobal: organizationId !== null,
        }
      : undefined,
    { enabled: canSearch }
  )

  const suggestions = useMemo(() => clubsQuery.data ?? [], [clubsQuery.data])
  const showResults = canSearch && open
  const showEmptyState = showResults && !clubsQuery.isFetching && suggestions.length === 0

  useEffect(() => {
    if (!canSearch) {
      setOpen(false)
    }
  }, [canSearch])

  const handleSelectClub = (club: TClub) => {
    onValueChange(club.name)
    onClubIdChange(club.id ?? null)
    setOpen(false)
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          value={value}
          disabled={disabled}
          placeholder={canSearch ? placeholder : 'Enter club name'}
          autoComplete="off"
          role="combobox"
          aria-expanded={showResults}
          aria-haspopup="listbox"
          aria-controls="club-selector-listbox"
          onFocus={() => {
            if (!disabled && canSearch) {
              setOpen(true)
            }
          }}
          onChange={event => {
            onValueChange(event.target.value)
            if (selectedClubId) {
              onClubIdChange(null)
            }
            if (!disabled && canSearch) {
              setOpen(true)
            }
          }}
          onBlur={() => {
            window.setTimeout(() => setOpen(false), 120)
          }}
          data-testid="club-selector-input"
        />
        {showResults && (
          <div className="absolute z-50 mt-2 max-h-60 w-full overflow-y-auto rounded-md border border-border bg-background shadow-lg">
            {clubsQuery.isFetching ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>
            ) : showEmptyState ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">No matching clubs</div>
            ) : (
              <ul role="listbox" id="club-selector-listbox" className="divide-y divide-border">
                {suggestions.map(club => (
                  <li key={club.id ?? club.name}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selectedClubId === club.id}
                      data-testid={`club-option-${club.id ?? club.name}`}
                      className={cn(
                        'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-muted focus:bg-muted',
                        selectedClubId === club.id ? 'bg-muted' : undefined
                      )}
                      onMouseDown={event => {
                        event.preventDefault()
                        handleSelectClub(club)
                      }}
                    >
                      <span className="font-medium text-foreground">{club.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {!isOnline && (
        <Badge variant="secondary" className="border-amber-200 bg-amber-100 text-amber-900">
          Offline: enter the club name manually
        </Badge>
      )}

      {!hasOrganization && isOnline && organizationId === null ? (
        <p className="text-xs text-muted-foreground">
          This show is not linked to an organization. Showing global clubs only; add an organization
          to scope suggestions.
        </p>
      ) : null}

      {selectedClubId && value ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Submitting saved club match.</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => {
              onValueChange('')
              onClubIdChange(null)
            }}
          >
            Clear
          </Button>
        </div>
      ) : null}

      {!selectedClubId && trimmedInput ? (
        <p className="text-xs text-muted-foreground">
          A new club named "{trimmedInput}" will be sent with this entry.
        </p>
      ) : null}
    </div>
  )
}
